import { Link } from "@tanstack/react-router"
import type { Tool } from "@/data/tools/tools-registry"
import { getCategoryLabel } from "@/data/tools/tools-registry"
import { HugeiconsIcon } from "@hugeicons/react"
import ArrowUpRight01Icon from "@hugeicons/core-free-icons/ArrowUpRight01Icon"
import { useTranslation } from "@/lib/i18n"

type ToolCardProps = {
  tool: Tool
}

export default function ToolCard({ tool }: ToolCardProps) {
  const { translations: t } = useTranslation()
  const isLive = tool.status === "live"
  const href = isLive ? `/tools/${tool.slug}` : "/tools"

  return (
    <Link
      to={href as "/tools"}
      className={`group relative flex flex-col p-5 sm:p-6 rounded-2xl border transition-all duration-300 ${
        isLive
          ? "border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900/50 hover:border-neutral-300 dark:hover:border-neutral-700 hover:shadow-lg hover:-translate-y-0.5 cursor-pointer"
          : "border-neutral-100 dark:border-neutral-800/50 bg-neutral-50/50 dark:bg-neutral-900/30 cursor-default opacity-70"
      }`}
      onClick={isLive ? undefined : (e) => e.preventDefault()}
    >
      {/* Category + Status */}
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
          {getCategoryLabel(tool.category)}
        </span>
        {isLive ? (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-800/40">
            {t.toolsPage.free}
          </span>
        ) : (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-neutral-100 dark:bg-neutral-800 text-neutral-500 dark:text-neutral-400">
            {t.toolsPage.comingSoon}
          </span>
        )}
      </div>

      {/* Name */}
      <h3 className="text-base sm:text-lg font-bold text-neutral-900 dark:text-white mb-1.5 group-hover:text-brand-button transition-colors">
        {tool.shortName}
      </h3>

      {/* Description */}
      <p className="text-sm text-neutral-600 dark:text-neutral-400 leading-relaxed flex-1">
        {tool.description}
      </p>

      {/* Tags */}
      <div className="flex flex-wrap gap-1.5 mt-3">
        {tool.tags.slice(0, 3).map((tag) => (
          <span
            key={tag}
            className="px-2 py-0.5 text-xs rounded-md bg-neutral-100 dark:bg-neutral-800 text-neutral-500 dark:text-neutral-400"
          >
            {tag}
          </span>
        ))}
      </div>

      {/* Hover arrow */}
      {isLive && (
        <div className="absolute top-5 right-5 opacity-0 group-hover:opacity-100 transition-opacity">
          <HugeiconsIcon icon={ArrowUpRight01Icon} size={20} color="currentColor" className="text-brand-button" />
        </div>
      )}
    </Link>
  )
}
